<template>
    <SimplePlane ref="refBg" :material="material" :layer="LAYER_BG" :bounds="bounds" />
</template>

<script setup>
    import { disposeMaterial } from '#imports';
    import { Color, RepeatWrapping } from 'three';
    import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';

    import { lerp } from '@resn/gozer-math';
    import { disposeObject } from '@resn/gozer-three/helpers';
    import { CustomMaterial } from '@resn/gozer-three/materials';
    import { usePane, useRafBool, useResize } from '@resn/gozer-vue';
    import gsap from '@resn/gsap';

    import glslPaletteLerp from '@/libs/lygia/color/palette/lerp.glsl';
    import glslFill from '@/libs/lygia/draw/fill.glsl';
    import glslConst from '@/libs/lygia/math/const.glsl';
    import glslCircleSdf from '@/libs/lygia/sdf/circleSDF.glsl';
    import glslRotate from '@/libs/lygia/space/rotate.glsl';
    import SimplePlane from '~/components/gl/common/SimplePlane.vue';
    import { glslSpiralVortex } from '~/components/gl/shaders/ShaderChunks/spiral-vortex';
    import { LAYER_BG } from '~/core/constants';
    import { createNoiseFromTexture2 } from '~/libs/three/noise-texture';
    import { useGlobalAssets } from '~/providers/GlobalAssets';

    const refBg = ref(null);

    const active = ref(true);
    const progress = ref(1);
    const uSwirlProgress = ref(0);
    const uSpinSpeed = ref(1);
    const uTwistAmount = ref(12);
    const uToonThreshold = ref(0.85);
    const uNoiseScale = ref(1.3);
    const uOffset = ref(0);

    const viewport = useResize();
    const bounds = computed(() => {
        const max = Math.max(viewport.width, viewport.height);
        return { width: max, height: max };
    });

    const color1 = ref('#F8BAFF');
    const color2 = ref('#273AFF');
    const color3 = ref('#000000');
    // const colors = [color1, color2, color2, color3, color3, color1];
    const colors = [color3, color1, color2, color3];

    onMounted(() => {
        refBg.value.mesh.position.z = 0.1;
    });

    const material = new CustomMaterial({
        uniforms: {
            uTime: 0,
            uProgress: 0,
            uSwirlProgress: 0,
            uTwistAmount: 0,
            uSpinSpeed: 0,
            uNoiseMap: null,
            uNoiseScale: 0,
            uGradientColors: colors.map(() => new Color()),
            uToonThreshold: 0,
            uOffset: 0,
        },
        fs: `
            uniform float uTime;
            uniform float uProgress;
            uniform float uSwirlProgress;
            uniform float uTwistAmount;
            uniform float uSpinSpeed;
            uniform sampler2D uNoiseMap;
            uniform vec3 uGradientColors[PALETTE_LERP_SIZE];
            uniform float uToonThreshold;
            uniform float uNoiseScale;
            uniform float uOffset;
            varying vec2 vUv;

            ${glslConst}
            ${glslCircleSdf}
            ${glslRotate}
            ${glslFill}
            ${glslPaletteLerp}
            ${glslSpiralVortex}
            ${createNoiseFromTexture2}

            void main() {
                float radius = length(vUv - 0.5);

                float spinSpeed = -uTime * uSpinSpeed;
                vec2 noiseUv = vUv;
                float spiralNoise = spiralWarpedNoise(
                    uNoiseMap,
                    noiseUv,
                    uNoiseScale,
                    spinSpeed,
                    uTwistAmount,
                    uOffset
                );
                
                float sdf = spiralNoise;
                sdf *= mix(0.0, 0.99, smoothstep(0.6, 0.0, radius));
                
                float mask = 1.0 - fill(sdf, mix(0.7, 0.0, uSwirlProgress));
                
                vec3 color = paletteLerp(uGradientColors, rotate(vUv, QTR_PI).x);
                
                float alpha = mask * uProgress;
                gl_FragColor = vec4(color, alpha);
                // gl_FragColor = vec4(vec3(mask), 1.0);
                
                #include <tonemapping_fragment>
                #include <colorspace_fragment>
            }
        `,
        options: {
            name: 'BackgroundGradient',
            depthWrite: false,
            depthTest: false,
            defines: {
                PALETTE_LERP_SIZE: colors.length,
            },
        },
    });

    useGlobalAssets(({ data }) => {
        const { noise1 } = data;
        noise1.wrapS = noise1.wrapT = RepeatWrapping;
        noise1.needsUpdate = true;

        material.uniforms.uNoiseMap.value = noise1;
    });

    let tl = null;
    const show = ({ delay = 0, swirl = true } = {}) => {
        tl?.kill();

        const duration = swirl ? 0.8 : 0.5;
        const ease = 'power2.out';

        tl = gsap.timeline({ delay });
        tl.fromTo(uSwirlProgress, { value: swirl ? 0 : 1 }, { value: 1, duration, ease }, 0);
        tl.fromTo(progress, { value: swirl ? 1 : 0 }, { value: 1, duration, ease }, 0);
    };

    const hide = ({ delay = 0, swirl = true } = {}) => {
        tl?.kill();

        const duration = swirl ? 0.5 : 0.5;
        const ease = 'power2.in';

        tl = gsap.timeline({ delay });
        tl.to(uSwirlProgress, { value: swirl ? 0 : 1, duration, ease }, 0);
        tl.to(progress, { value: swirl ? 1 : 0, duration, ease }, 0);
    };

    watchEffect(() => {
        material.uniforms.uProgress.value = progress.value;
        material.uniforms.uSwirlProgress.value = uSwirlProgress.value;
        material.uniforms.uTwistAmount.value = uTwistAmount.value;
        material.uniforms.uSpinSpeed.value = uSpinSpeed.value;
        material.uniforms.uToonThreshold.value = uToonThreshold.value;
        material.uniforms.uNoiseScale.value = uNoiseScale.value;
        material.uniforms.uOffset.value = uOffset.value;
    });

    watchEffect(() => {
        const colors = material.uniforms.uGradientColors.value;
        colors[0].set(color3.value);
        colors[1].set(color1.value);
        colors[2].set(color2.value);
        colors[3].set(color3.value);
    });

    watch(active, (val) => {
        if (val) show();
        else hide();
    });

    useRafBool(active, ({ timestamp }) => {
        material.uniforms.uTime.value = timestamp * 0.001;
    });

    const destroy = () => {
        disposeMaterial(material);
    };

    onBeforeUnmount(destroy);

    defineExpose({ show, hide });

    const paneOptions = {
        min: 0,
        max: 1,
        step: 0.01,
    };

    usePane(
        [
            { name: 'progress', value: progress, options: paneOptions },
            { name: 'uSwirlProgress', value: uSwirlProgress, options: paneOptions },
            { name: 'uTwistAmount', value: uTwistAmount, options: { min: 0, max: 20, step: 0.01 } },
            { name: 'uSpinSpeed', value: uSpinSpeed, options: { min: 0, max: 3, step: 0.01 } },
            { name: 'uToonThreshold', value: uToonThreshold, options: paneOptions },
            { name: 'uNoiseScale', value: uNoiseScale, options: { min: 0, max: 3, step: 0.01 } },
            { name: 'uOffset', value: uOffset, options: paneOptions },
            { name: 'active', value: active },
            { name: 'color1', value: color1 },
            { name: 'color2', value: color2 },
            { name: 'color3', value: color3 },
        ],
        { title: 'Background Gradient' }
    );
</script>
